import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Building2,
  Users,
  BarChart3,
  Shield,
  Wallet,
  Heart,
  Clock,
  Menu,
  X
} from 'lucide-react';

const ForCorporatesPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', padding: '0 24px' },
    nav: { borderBottom: '1px solid #e8eaed', padding: '16px 0', position: 'sticky' as const, top: 0, zIndex: 1000, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' },
    navContainer: { maxWidth: '1200px', margin: '0 auto', padding: '0 16px', display: 'flex', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: 'center', gap: isMobile ? '12px' : '0' },
    logo: { fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#14B8A6', textDecoration: 'none', letterSpacing: '-0.5px' },
    navLinks: { display: 'flex', gap: '32px', alignItems: 'center' },
    mobileMenu: { position: 'fixed' as const, top: 0, left: 0, width: '80%', maxWidth: '400px', height: '100vh', background: '#ffffff', zIndex: 2000, padding: '80px 24px 24px', display: 'flex', flexDirection: 'column' as const, gap: '24px' },
    mobileMenuOverlay: { position: 'fixed' as const, top: 0, left: 0, width: '100%', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1999 },
    hamburger: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    closeButton: { position: 'absolute' as const, top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    navLink: { color: '#5f6368', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' },
    primaryButton: { background: '#14B8A6', color: '#ffffff', padding: '12px 24px', borderRadius: '24px', textDecoration: 'none', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)' },
  };

  const problems = [
    { stat: '73%', desc: 'of corporate gym memberships go unused within 3 months' },
    { stat: '₹15K+', desc: 'wasted per employee per year on unused memberships' },
    { stat: '1 in 5', desc: 'employees actually use traditional wellness benefits' },
  ];

  const benefits = [
    { icon: <Users size={24} color="#ffffff" />, title: 'Multi-Activity Access', desc: 'Employees choose from gym, yoga, swimming, turf, dance and more — not just one gym. Engagement stays high because variety prevents boredom.' },
    { icon: <Wallet size={24} color="#ffffff" />, title: 'Pay-Per-Use Economics', desc: 'Fund only what employees actually use. No more paying for 100 memberships when only 20 people go. Multiple flexible pricing models available.' },
    { icon: <BarChart3 size={24} color="#ffffff" />, title: 'Real-Time Reporting', desc: 'Track utilisation, popular activities, active employees, and spending — all in a live Corporate Admin Dashboard. Anonymised data respects privacy.' },
    { icon: <Shield size={24} color="#ffffff" />, title: 'Zero-Risk Pilot', desc: 'Start with a pilot program at no upfront cost. Pay only for employees who actually book sessions. Scale up only when you see results.' },
    { icon: <Heart size={24} color="#ffffff" />, title: 'Employee Satisfaction', desc: 'Employees love the freedom of choice. No more "assigned" gym they never visit. They pick what they want, when they want.' },
    { icon: <Clock size={24} color="#ffffff" />, title: 'Effortless Administration', desc: 'No vendor management, no contracts with individual gyms, no reimbursement paperwork. HUSLRZ handles everything centrally.' },
  ];

  const howItWorks = [
    { step: '01', title: 'Choose a Plan', desc: 'We work with you to design a wellness budget that fits — fixed, co-contribution, or pay-per-active models available.' },
    { step: '02', title: 'Enrol Employees', desc: 'Bulk upload or invite employees. Each gets a HUSLRZ account with corporate wellness credits.' },
    { step: '03', title: 'Employees Book & Go', desc: 'Employees discover facilities on the HUSLRZ app, book sessions, and check in with a QR code. No reimbursement needed.' },
    { step: '04', title: 'Track & Optimise', desc: 'Monitor engagement, utilisation, and ROI from your Corporate Dashboard. We send monthly reports automatically.' },
  ];

  const faqs = [
    { q: 'What is the minimum company size to use HUSLRZ?', a: 'HUSLRZ corporate wellness works for companies of any size — from 10-person startups to 10,000+ employee enterprises. We have plans designed for every scale.' },
    { q: 'How much does it cost?', a: 'Pricing is flexible based on your chosen model and headcount. We offer fixed annual, monthly, co-contribution, and pay-per-active models. Contact our team for a custom proposal.' },
    { q: 'Can we start with a pilot before committing?', a: 'Absolutely. We encourage it. Run a 30-60 day pilot with a subset of employees at minimal or zero upfront cost. Scale only when you see the engagement data.' },
    { q: 'How is employee data handled?', a: 'HUSLRZ is compliant with India\'s Digital Personal Data Protection (DPDP) Act, 2023. Corporate admins see anonymised, aggregated reports only. Individual booking details are never shared with employers.' },
    { q: 'What activities can employees access?', a: 'All categories on the HUSLRZ platform — gym, yoga, swimming, turf, courts, CrossFit, dance, personal training, and more. Across all verified partner facilities in their city.' },
    { q: 'How is billing handled?', a: 'You receive a single monthly or quarterly GST-compliant invoice from HUSLRZ. No dealing with dozens of gym invoices. TDS compliance is handled.' },
  ];

  return (
    <>
      <Helmet>
        <title>Corporate Wellness — HUSLRZ | Flexible Employee Fitness Benefits</title>
        <meta name="description" content="Employee wellness that actually works. Multi-activity access, pay-per-use economics, real-time reporting. Start a zero-risk pilot today." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>

      <div style={{ fontFamily: 'Google Sans, Roboto, Arial, sans-serif', color: '#202124' }}>
        {/* Navigation */}
        <nav style={styles.nav}>
          <div style={styles.navContainer}>
            {isMobile && <button style={styles.hamburger} onClick={() => setMobileMenuOpen(true)}><Menu size={24} color="#202124" /></button>}
            <Link to="/" style={styles.logo}>HUSLRZ</Link>
            {!isMobile && (
              <div style={styles.navLinks}>
                <Link to="/partners" style={styles.navLink}>For Partners</Link>
                <Link to="/corporate" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>For Corporates</Link>
                <Link to="/about" style={styles.navLink}>About</Link>
                <Link to="/contact" style={styles.navLink}>Contact</Link>
                <Link to="/login" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>Login</Link>
                <Link to="/signup" style={styles.primaryButton}>
                  Get Started <ArrowRight size={16} />
                </Link>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <>
            <div style={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)} />
            <div style={styles.mobileMenu}>
              <button style={styles.closeButton} onClick={() => setMobileMenuOpen(false)}><X size={24} color="#202124" /></button>
              <Link to="/partners" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Partners</Link>
              <Link to="/corporate" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>For Corporates</Link>
              <Link to="/about" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/contact" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <Link to="/signup" style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', padding: '16px 24px', fontSize: '16px', marginTop: '16px' }} onClick={() => setMobileMenuOpen(false)}>
                Get Started <ArrowRight size={18} />
              </Link>
            </div>
          </>
        )}

        {/* Hero */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', textAlign: 'center' as const, background: '#ffffff' }}>
          <div style={styles.container}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#e6f7f5', color: '#14B8A6', padding: '8px 16px', borderRadius: '24px', fontSize: '14px', fontWeight: 500, marginBottom: '32px' }}>
              <Building2 size={16} /> Zero-risk pilot available
            </div>
            <h1 style={{ fontSize: 'clamp(32px, 7vw, 56px)', fontWeight: 400, color: '#202124', marginBottom: '24px', lineHeight: 1.15, letterSpacing: '-0.5px' }}>
              Employee wellness<br />that actually <span style={{ color: '#14B8A6' }}>works</span>
            </h1>
            <p style={{ fontSize: isMobile ? '16px' : '20px', color: '#5f6368', lineHeight: 1.6, maxWidth: '680px', margin: '0 auto 40px' }}>
              Give your team flexible access to gyms, yoga, swimming, turf and more.
              Pay only for what they use. Track engagement in real time.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="/corporate-survey" style={{ ...styles.primaryButton, padding: '16px 32px', fontSize: '16px' }}>
                Get Started — Free Demo <ArrowRight size={18} />
              </a>
              <a href="mailto:support@huslrz.com" style={{ background: '#ffffff', color: '#14B8A6', padding: '16px 32px', borderRadius: '24px', textDecoration: 'none', fontSize: '16px', fontWeight: 600, border: '1px solid #dadce0', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                Email support@huslrz.com
              </a>
            </div>
          </div>
        </section>

        {/* Problem Statement */}
        <section style={{ padding: isMobile ? '48px 16px' : '64px 24px', background: '#1F2937', color: '#ffffff' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(22px, 5vw, 32px)', fontWeight: 400, marginBottom: '40px', textAlign: 'center' as const }}>
              Traditional corporate wellness is broken
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '24px' : '40px', textAlign: 'center' as const }}>
              {problems.map((p, i) => (
                <div key={i}>
                  <div style={{ fontSize: isMobile ? '40px' : '56px', fontWeight: 500, color: '#14B8A6', marginBottom: '8px' }}>{p.stat}</div>
                  <p style={{ fontSize: '15px', color: '#D1D5DB', lineHeight: 1.6, margin: 0 }}>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: '#f8f9fa' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '12px', textAlign: 'center' as const }}>
              The HUSLRZ approach
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '18px', color: '#5f6368', textAlign: 'center' as const, maxWidth: '600px', margin: '0 auto 48px' }}>
              Flexible, measurable, and employee-loved
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '16px' : '20px' }}>
              {benefits.map((b, i) => (
                <div key={i} style={{ background: '#ffffff', borderRadius: '16px', padding: isMobile ? '24px' : '32px', border: '1px solid #e8eaed' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                    {b.icon}
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>{b.title}</h3>
                  <p style={{ fontSize: '14px', color: '#5f6368', lineHeight: 1.7, margin: 0 }}>{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: '#ffffff' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '12px', textAlign: 'center' as const }}>
              How it works for your organisation
            </h2>
            <p style={{ fontSize: isMobile ? '15px' : '18px', color: '#5f6368', textAlign: 'center' as const, maxWidth: '500px', margin: '0 auto 48px' }}>
              Up and running in under a week
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: isMobile ? '24px' : '32px' }}>
              {howItWorks.map((s, i) => (
                <div key={i} style={{ textAlign: 'center' as const }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#e6f7f5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '20px', fontWeight: 700, color: '#14B8A6' }}>
                    {s.step}
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>{s.title}</h3>
                  <p style={{ fontSize: '14px', color: '#5f6368', lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section style={{ padding: isMobile ? '48px 16px' : '64px 24px', background: '#f8f9fa' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px' }}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 36px)', fontWeight: 400, color: '#202124', marginBottom: '40px', textAlign: 'center' as const }}>
              What every corporate plan includes
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              {[
                'Access to all HUSLRZ partner facilities',
                'Corporate Admin Dashboard',
                'Employee onboarding support',
                'Dedicated account manager',
                'Monthly utilisation reports',
                'Single GST-compliant invoice',
                'DPDP Act compliant data handling',
                'Flexible budget allocation per employee',
                'Custom wellness challenges & leaderboards',
                'Priority customer support',
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#ffffff', borderRadius: '12px', border: '1px solid #e8eaed' }}>
                  <CheckCircle2 size={18} color="#14B8A6" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '14px', color: '#374151' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: '#ffffff' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '48px', textAlign: 'center' as const }}>
              Frequently asked questions
            </h2>
            {faqs.map((faq, i) => (
              <div key={i} style={{ borderBottom: '1px solid #e8eaed', paddingBottom: '24px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#202124', marginBottom: '8px' }}>{faq.q}</h3>
                <p style={{ fontSize: '15px', color: '#5f6368', lineHeight: 1.7, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)', textAlign: 'center' as const, color: '#ffffff' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(28px, 6vw, 48px)', fontWeight: 400, marginBottom: '16px' }}>
              Ready to transform your employee wellness?
            </h2>
            <p style={{ fontSize: isMobile ? '16px' : '18px', marginBottom: '40px', opacity: 0.95, maxWidth: '550px', margin: '0 auto 40px' }}>
              Schedule a 15-minute demo or start a zero-risk pilot with your team today.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="/corporate-survey" style={{ background: '#ffffff', color: '#14B8A6', padding: '16px 32px', borderRadius: '24px', textDecoration: 'none', fontSize: '16px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                Get Started — Free Demo <ArrowRight size={18} />
              </a>
              <a href="mailto:support@huslrz.com" style={{ background: 'transparent', color: '#ffffff', padding: '16px 32px', borderRadius: '24px', textDecoration: 'none', fontSize: '16px', fontWeight: 600, border: '2px solid #ffffff', display: 'inline-flex', alignItems: 'center' }}>
                Email support@huslrz.com
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ background: '#111827', padding: isMobile ? '40px 16px' : '64px 24px', color: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? '32px' : '40px', marginBottom: '40px' }}>
              <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#14B8A6', marginBottom: '12px' }}>HUSLRZ</div>
                <p style={{ fontSize: '14px', color: '#9CA3AF', lineHeight: 1.6, margin: 0 }}>India's pay-per-use fitness marketplace. Fitness without commitment.</p>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Platform</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px' }}><Link to="/partners" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>For Partners</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/corporate" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>For Corporates</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/about" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>About Us</Link></li>
                  <li><Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Contact</Link></li>
                </ul>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Legal</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px' }}><Link to="/privacy" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/terms" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Terms of Service</Link></li>
                  <li><Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Refund Policy</Link></li>
                </ul>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Get in Touch</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px', fontSize: '14px', color: '#9CA3AF' }}>support@huslrz.com</li>
                  <li style={{ fontSize: '14px', color: '#9CA3AF' }}>Chennai, Tamil Nadu</li>
                </ul>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #374151', paddingTop: '24px', textAlign: 'center' as const }}>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>&copy; {new Date().getFullYear()} HUSLRZ. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ForCorporatesPage;
